import { useQueryClient } from "@tanstack/react-query";
import { SocketsContext } from "libs/soket";
import { useContext, useEffect } from "react";

import { useGetActiveTicketsCount } from "../../../../aiTickets/apis/useGetActiveTicketsCount";
import { SUPPORT_TICKETS_QUERY_KEY } from "../../../../aiTickets/constants/enums";
import { ModeratorRoutesPrivileges } from "../../../constants";
import { useHasPrivilege } from "../../useHasPrivilege";

/**
 * Sidebar badge for the "AI Tickets" route. Backed by
 * `GET /admin/support-tickets/active-count?createdByAi=true` which counts
 * tickets whose status is NOT in (Resolved, Closed). Counter therefore stays
 * stable across Open → InProgress → WaitingForCustomer transitions and only
 * drops when the admin moves a ticket to Resolved (or Closed) — matches the
 * client requirement.
 *
 * Real-time: piggybacks on the existing `support-tickets-admin` socket. Any
 * `support-ticket.created` or `support-ticket.updated` invalidates the
 * active-count query so the badge re-fetches with up-to-date data.
 */
export const useAiTicketsCounter = () => {
  const { supportTicketsAdminSocket } = useContext(SocketsContext);
  const queryClient = useQueryClient();
  const canAccess = useHasPrivilege(ModeratorRoutesPrivileges.AiAssistants);
  const { data } = useGetActiveTicketsCount(
    { createdByAi: true },
    { enabled: canAccess }
  );

  useEffect(() => {
    const socket = supportTicketsAdminSocket.socket;
    if (!socket) return;

    const refetchCount = () =>
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_TICKETS_QUERY_KEY, "active-count"]
      });

    socket.on("support-ticket.created", refetchCount);
    socket.on("support-ticket.updated", refetchCount);

    return () => {
      socket.off("support-ticket.created", refetchCount);
      socket.off("support-ticket.updated", refetchCount);
    };
  }, [supportTicketsAdminSocket.socket]);

  return { aiTicketsCount: data?.count ?? 0 };
};
